/**
 * RecentChats — the last few Vee conversations, right on the dashboard.
 * Clicking one reopens it in the chat (AgentPage consumes openSessionId from
 * nav state, same handoff pattern as document deep-links). Renders nothing
 * when there's no history — no empty-state noise on a fresh account.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronRight, FiGitBranch, FiMessageSquare } from 'react-icons/fi';
import SectionCard from './SectionCard';
import TimeAgo from '../common/TimeAgo';
import { aiAPI } from '../../services/api';
import { getView, setView, hasView } from '../../services/viewCache';
import { ROUTES } from '../../constants/constants';

// Stale-while-revalidate: the dashboard remounts on every visit, so cache the
// last few sessions module-side and render them instantly on return.
const CACHE_KEY = 'dashboard:recentChats';

const timeAgo = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

export default function RecentChats() {
  const navigate = useNavigate();
  // null = never loaded yet, [] = none, [..] = cached rows rendered instantly.
  const [sessions, setSessions] = useState(() => getView(CACHE_KEY) ?? null);

  useEffect(() => {
    let cancelled = false;
    aiAPI.listChatSessions()
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.sessions || res?.sessions || [];
        const top = Array.isArray(list) ? list.slice(0, 3) : [];
        setSessions(top);
        setView(CACHE_KEY, top);
      })
      // Keep whatever we already showed on a failed refresh; only fall to the
      // empty state on a genuine first load.
      .catch(() => { if (!cancelled && !hasView(CACHE_KEY)) setSessions([]); });
    return () => { cancelled = true; };
  }, []);

  if (!sessions || sessions.length === 0) return null;

  return (
    <SectionCard
      icon="ai"
      iconTone="#145f5f"
      title="Recent Conversations"
      action={{ label: 'All chats', onClick: () => navigate(ROUTES.AGENT_HISTORY) }}
      className="mt-3.5"
    >
      <div className="p-3 flex flex-col gap-1.5">
        {sessions.map((s) => {
          const isFork = String(s.title || '').startsWith('[Fork]');
          const title = isFork ? String(s.title).slice(6).trim() : s.title;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => navigate(ROUTES.AGENT, { state: { openSessionId: s.id } })}
              className="group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] text-left hover:bg-[#faf7f2] transition-colors"
            >
              <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(20,95,95,0.08)] text-[#145f5f]">
                <FiMessageSquare size={13} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#08111f]">
                  {isFork && (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.5px] px-1.5 py-[1px] rounded-md" style={{ background: 'rgba(184,151,58,0.14)', color: '#8a6a1f' }}>
                      <FiGitBranch size={8} /> Fork
                    </span>
                  )}
                  <span className="truncate">{title}</span>
                </span>
                <span className="block text-[10px] text-[#9a9080]"><TimeAgo iso={s.updatedAt}>{timeAgo(s.updatedAt)}</TimeAgo>{s.docCount > 0 ? ` · ${s.docCount} doc${s.docCount === 1 ? '' : 's'}` : ''}</span>
              </span>
              <FiChevronRight size={13} className="shrink-0 text-[#c9c0ad] group-hover:text-[#145f5f] transition-colors" />
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
}
